import type { PaginationMeta } from '@sr-sdks/api-sdk-axios';

/**
 * A list's page count when its total may be capped. The titles list stops
 * counting at a limit and flags it (`meta.totalItemsCapped`, APP-2589), so its
 * `totalPages` is a floor: a page past it that came back with rows exists, and
 * a full one means another may follow. Without the flag this is `totalPages`.
 *
 * An empty page past the floor falls back to the floor, the last page known to
 * exist: finding the real last page from here would take a request per page.
 * So Next from a full page that happens to be the last also lands there.
 */
export const pageCountFloor = (meta?: Partial<PaginationMeta>) => {
  const totalPages = meta?.totalPages ?? 0;

  if (!meta?.totalItemsCapped || !meta.itemCount || !meta.currentPage) {
    return totalPages;
  }

  const isFull = meta.itemCount === meta.itemsPerPage;

  return Math.max(totalPages, meta.currentPage + (isFull ? 1 : 0));
};

/**
 * What a paged list on the titles endpoint reads from `meta` to show and page
 * its total. Each takes them from here, so none shows a capped total as exact
 * or sends a page past it back to the floor.
 */
export const cappedPagination = (meta?: Partial<PaginationMeta>) => ({
  isTotalCapped: meta?.totalItemsCapped === true,
  itemCount: meta?.itemCount ?? 0,
  totalItems: meta?.totalItems ?? 0,
  totalPages: pageCountFloor(meta),
});

/**
 * "10,000+" for a capped total, else "10,000", with `CountBadge`'s separators.
 * The toolbar range keeps `PaginationRange`'s bare numbers ("26-50 of 10000+").
 */
export const formatCappedTotal = (total: number, isCapped?: boolean) =>
  `${total.toLocaleString('en-AU')}${isCapped ? '+' : ''}`;
